import {
  useMutation,
  useQuery
} from "@tanstack/react-query";
import {
  AxiosError,
  AxiosResponse
} from "axios";
import {
  useParams
} from "react-router-dom";
import {
  createSelector
} from "reselect";

import {
  useApiParams
} from "src/app/hooks";
import {
  BaseResponse,
  Pagination,
  Response,
  ResponseFieldError,
  ResponseList
} from "src/app/models/api.types";
import {
  RewardWarningTransactionModel,
  RewardsModel,
  RewardsParkingModel
} from "../models/Rewards.model";
import {
  RewardPathType,
  RewardType
} from "../page/RewardTabs/RewardTabs.types";
import {
  URL_REWARDS,
  deleteRewards,
  getRewardWarningTransaction,
  getRewards,
  getRewardsDetail,
  getRewardsParking,
  saveRewards,
  saveRewardsTicket,
  validateBankRewards
} from '../services/Rewards.service';
import { formatPartnerLabel } from "../page/Rewards/Rewards.utils";

interface UseGetRewardsParams {
  reward_types?: string[];
  statuses?: string[]
}

// get rewards
const selectRewards = createSelector(
  (state: AxiosResponse<BaseResponse<Pagination<RewardsModel[]>>>) => state.data,
  (data: BaseResponse<Pagination<RewardsModel[]>>) => data.response_output?.list
);
export function useGetRewards(defaultParams?: UseGetRewardsParams) {

  const defaultValues = {
    directory_ids: '',
    reward_types: '',
    months: '',
    period: ''
  };

  const [apiParams] = useApiParams({ defaultValues });
  const { path } = useParams<{ path: RewardPathType }>();
  const _params = {
    ...defaultParams,
    ...apiParams
  };
  const params = {
    ..._params,
    reward_start_date: _params.start_date,
    reward_end_date: _params.end_date,
    reward_types: !RewardType[path] ? _params.reward_types : [RewardType[path]],
    start_date: undefined,
    end_date: undefined,
    months: [_params.months]
  };

  return useQuery<
    AxiosResponse<BaseResponse<Pagination<RewardsModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<RewardsModel[]> | undefined
  >({
    keepPreviousData: false,
    queryKey: [URL_REWARDS, params],
    enabled: path !== 'featured-rewards' && path !== 'voucher-group',
    queryFn: (_) => getRewards(params, _.signal),
    select: selectRewards,
  });
}

// get rewards detail
const selectRewardsDetail = createSelector(
  (state: AxiosResponse<BaseResponse<Response<RewardsModel>>>) => state.data,
  (data: BaseResponse<Response<RewardsModel>>) => {
    const { directory, ...rest } = data.response_output?.detail ?? {};
    const newDirectory = {
      label: directory?.directory_name,
      value: directory?.id,
      ...directory
    }

    const partners = rest.partners?.map((p: any) => ({
      ...p,
      label: formatPartnerLabel(p),
      value: p?.id || p?.value,
    }));

    return {
      ...rest,
      directory: newDirectory,
      partners: partners ?? rest.partners,
    };
  }
);
export function useGetRewardsDetail(isTicket?: boolean) {

  const params = useParams<{ id: string }>();
  return useQuery<
    AxiosResponse<BaseResponse<Response<RewardsModel>>>,
    AxiosError<BaseResponse<Response<any>>>,
    RewardsModel | undefined
  >({
    keepPreviousData: false,
    queryKey: [`${URL_REWARDS}/${params.id}${isTicket ? '/tickets' : ''}`],
    queryFn: (_) => getRewardsDetail(params.id, _.signal, isTicket),
    select: selectRewardsDetail,
    enabled: !!params.id
  });
}

// get rewards parking
const selectRewardsParking = createSelector(
  (state: AxiosResponse<BaseResponse<Pagination<RewardsParkingModel[]>>>) => state.data,
  (data: BaseResponse<Pagination<RewardsParkingModel[]>>) => data.response_output?.list
);
export function useGetRewardsParking() {

  const { path, id } = useParams<{ path: RewardPathType, id: string }>();
  const isFreeParking = path === 'free-parking';
  const defaultValues = isFreeParking ? {} : { limit: undefined, page: undefined };
  const [params] = useApiParams({ defaultValues });

  return useQuery<
    AxiosResponse<BaseResponse<Pagination<RewardsParkingModel[]>>>,
    AxiosError<BaseResponse<Response<any>>>,
    ResponseList<RewardsParkingModel[]> | undefined
  >({
    keepPreviousData: true,
    queryKey: [`${URL_REWARDS}/${id}/parking`, { ...params, id }],
    queryFn: (_) => getRewardsParking({ ...params, id }, _.signal),
    select: selectRewardsParking,
    enabled: isFreeParking
  });
}

const selectRewardWarningTransaction = createSelector(
  (state: AxiosResponse<BaseResponse<Response<RewardWarningTransactionModel>>>) => state.data,
  (data: BaseResponse<Response<RewardWarningTransactionModel>>) => data.response_output?.detail
);
export function useGetRewardWarningTransaction(id?: string) {

  const params = useParams<{ customerId: string }>();

  return useQuery<
    AxiosResponse<BaseResponse<Response<RewardWarningTransactionModel>>>,
    AxiosError<BaseResponse<Response<any>>>,
    RewardWarningTransactionModel | undefined
  >({
    keepPreviousData: false,
    queryKey: [URL_REWARDS, id, params.customerId],
    queryFn: (_) => getRewardWarningTransaction(id, params.customerId, _.signal),
    enabled: !!id && !!params.customerId,
    select: selectRewardWarningTransaction
  });
}

// save rewards
export function useSaveRewards() {

  return useMutation<
    AxiosResponse<BaseResponse<Response<RewardsModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    RewardsModel
  >({
    mutationKey: [URL_REWARDS],
    mutationFn: saveRewards
  });
}

// save rewards E-TICKET
export function useSaveRewardsTicket() {

  return useMutation<
    AxiosResponse<BaseResponse<Response<RewardsModel>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    RewardsModel
  >({
    mutationKey: [URL_REWARDS + '/tickets'],
    mutationFn: saveRewardsTicket
  });
}

// delete rewards
export function useDeleteRewards() {

  return useMutation<
    AxiosResponse<BaseResponse<Response<{}>>>,
    AxiosError<BaseResponse<Response<any>>>,
    any
  >({
    mutationKey: [URL_REWARDS],
    mutationFn: deleteRewards
  });
}

export function useValidateBankRewards() {

  return useMutation<
    AxiosResponse<BaseResponse<Response<{}>>>,
    AxiosError<BaseResponse<ResponseFieldError>>,
    any
  >({
    mutationKey: [URL_REWARDS],
    mutationFn: validateBankRewards,
  });
}
